import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Users, Search, ChevronRight, Trophy, Shield } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const TeamCard = ({ team }) => (
  <Link
    to={`/teams/${team.team_id}`}
    className="group text-center p-6 bg-white hover:bg-gray-50 border border-gray-100 hover:border-[#FFC72C] hover:shadow-xl transition-all"
    data-testid={`team-card-${team.team_id}`}
  >
    <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
      <img
        src={team.logo_url || "https://placehold.co/100x100/09090b/FFF?text=TEAM"}
        alt={team.name}
        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
      />
    </div>
    <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors mb-1">
      {team.short_name}
    </h3>
    <p className="text-gray-500 text-sm mb-2">{team.name}</p>
    {team.founded && <p className="text-xs text-gray-400">Est. {team.founded}</p>}
    <div className="flex items-center justify-center text-[#009E60] font-medium text-sm mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
      View Team <ChevronRight className="w-4 h-4 ml-1" />
    </div>
  </Link>
);

const CompetitionSection = ({ competition, teams, colorClass }) => {
  if (!teams.length) return null;
  return (
    <section className="mb-14" data-testid={`competition-section-${competition.competition_id}`}>
      <div className={`${colorClass} text-white px-6 py-4 flex items-center gap-3 mb-6`}>
        <Trophy className="w-5 h-5" />
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-black uppercase text-lg md:text-xl tracking-tight">{competition.name}</h2>
          {competition.season && (
            <p className="text-[10px] uppercase tracking-widest opacity-80">Season {competition.season}</p>
          )}
        </div>
        <Badge className="bg-white/20 text-white hover:bg-white/20">{teams.length} clubs</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {teams.map((t) => <TeamCard key={t.team_id} team={t} />)}
      </div>
    </section>
  );
};

const competitionColor = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("premier")) return "bg-[#009E60]";
  if (n.includes("first")) return "bg-[#FFC72C] text-black";
  if (n.includes("second")) return "bg-blue-600";
  if (n.includes("women")) return "bg-pink-600";
  if (n.includes("youth")) return "bg-purple-600";
  return "bg-[#09090b]";
};

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [tRes, cRes] = await Promise.all([
          axios.get(`${API}/teams`),
          axios.get(`${API}/competitions`),
        ]);
        setTeams(tRes.data || []);
        setCompetitions(cRes.data || []);
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredTeams = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => t.name.toLowerCase().includes(q) || t.short_name.toLowerCase().includes(q));
  }, [teams, searchQuery]);

  // Group teams by competition (a team can be in multiple → shown in each)
  const grouped = useMemo(() => {
    const map = {};
    competitions.forEach((c) => { map[c.competition_id] = []; });
    const unassigned = [];
    filteredTeams.forEach((t) => {
      const cids = (t.active_competition_ids || t.competition_ids || []).filter((cid) => map[cid]);
      if (!cids.length) unassigned.push(t);
      else cids.forEach((cid) => map[cid].push(t));
    });
    return { map, unassigned };
  }, [filteredTeams, competitions]);

  const totalGrouped = Object.values(grouped.map).reduce((a, b) => a + b.length, 0);
  const hasAnyGrouping = totalGrouped > 0;

  return (
    <div data-testid="teams-page">
      {/* Hero */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Users className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">Our Clubs</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Every registered ABFA club, organised by the competition they play in.
          </p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search clubs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 bg-white/10 border-0 text-white placeholder:text-gray-500 rounded-none"
              data-testid="team-search-input"
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {[1,2,3,4,5,6,7,8].map((i) => (
                <div key={i} className="text-center">
                  <Skeleton className="w-32 h-32 mx-auto mb-4" />
                  <Skeleton className="h-6 w-3/4 mx-auto mb-2" />
                </div>
              ))}
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">
                {searchQuery ? "No Teams Found" : "No Teams Registered"}
              </h3>
              <p className="text-gray-500">
                {searchQuery ? "Try a different search term." : "Check back soon for team registrations."}
              </p>
            </div>
          ) : hasAnyGrouping ? (
            <>
              {competitions.map((c) => (
                <CompetitionSection
                  key={c.competition_id}
                  competition={c}
                  teams={grouped.map[c.competition_id] || []}
                  colorClass={competitionColor(c.name)}
                />
              ))}
              {grouped.unassigned.length > 0 && (
                <section className="mb-14" data-testid="section-unassigned">
                  <div className="bg-gray-500 text-white px-6 py-4 flex items-center gap-3 mb-6">
                    <Shield className="w-5 h-5" />
                    <div className="flex-1">
                      <h2 className="font-heading font-black uppercase text-lg md:text-xl tracking-tight">Unassigned Clubs</h2>
                      <p className="text-[10px] uppercase tracking-widest opacity-80">Not yet placed in a competition</p>
                    </div>
                    <Badge className="bg-white/20 text-white hover:bg-white/20">{grouped.unassigned.length} clubs</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {grouped.unassigned.map((t) => <TeamCard key={t.team_id} team={t} />)}
                  </div>
                </section>
              )}
            </>
          ) : (
            // No teams have been assigned to any competition yet — flat list
            <>
              <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-[#FFC72C] text-sm text-gray-700" data-testid="notice-no-assignments">
                No clubs are yet assigned to a competition. An admin can arrange them via
                <strong> Admin → Team Assignments</strong>.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredTeams.map((t) => <TeamCard key={t.team_id} team={t} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default TeamsPage;
